import { generateMergedTickets, generateTicket, TicketDetailsDTO } from 'api/ticket'
import { useState } from 'react'
import { normalizeName } from 'utils/normalizeName'
import { v4 as uuidv4 } from 'uuid'

export const useTickets = (currentEvent: any, selectedSeats: string[]) => {
    const [loadingSeatIds, setLoadingSeatIds] = useState<{ [key: string]: boolean }>({})
    const [isLoadingAll, setIsLoadingAll] = useState<boolean>(false)

    const getTicketDetails = (seatId: string): TicketDetailsDTO | null => {
        const ticketInfo = currentEvent.tickets?.find((price: any) => price.seat_id === seatId)

        if (ticketInfo) {
            return {
                eventDetails: currentEvent,
                ticketId: ticketInfo.ticket_id,
            }
        } else {
            console.error(`Seat data not found for seatId: ${seatId}`)
            return null
        }
    }

    const prepareTicketDetails = (seats: string[]): TicketDetailsDTO[] => {
        return seats.map(getTicketDetails).filter((details) => details !== null) as TicketDetailsDTO[]
    }

    const downloadFile = (url: string, fileName: string) => {
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
    }

    const handleGenerateTicket = async (seatId: string): Promise<string | undefined> => {
        setLoadingSeatIds((prev) => ({ ...prev, [seatId]: true }))

        const ticketDetails = getTicketDetails(seatId)
        if (!ticketDetails) {
            setLoadingSeatIds((prev) => ({ ...prev, [seatId]: false }))
            return
        }

        try {
            const pdfBlob = await generateTicket(ticketDetails)
            const url = window.URL.createObjectURL(pdfBlob)

            const normalizedEventName = normalizeName(currentEvent.show.name)
            const uniqueFileName = `jegy_${normalizedEventName}_${uuidv4()}.pdf`

            downloadFile(url, uniqueFileName)
            return url
        } catch (error) {
            console.error('Failed to generate ticket:', error)
            throw error
        } finally {
            setLoadingSeatIds((prev) => ({ ...prev, [seatId]: false }))
        }
    }

    const handleDownloadTicket = async (seatId: string) => {
        setLoadingSeatIds((prev) => ({ ...prev, [seatId]: true }))

        const ticketDetails = getTicketDetails(seatId)
        if (!ticketDetails) {
            setLoadingSeatIds((prev) => ({ ...prev, [seatId]: false }))
            return
        }

        try {
            const pdfBlob = await generateTicket(ticketDetails)
            const url = window.URL.createObjectURL(pdfBlob)

            const normalizedEventName = normalizeName(currentEvent.show.name)
            const uniqueFileName = `jegy_${normalizedEventName}_${uuidv4()}.pdf`

            downloadFile(url, uniqueFileName)
        } catch (error) {
            console.error('Failed to download ticket:', error)
            throw error
        } finally {
            setLoadingSeatIds((prev) => ({ ...prev, [seatId]: false }))
        }
    }

    const handlePrintTicket = async (seatId: string) => {
        const pdfUrl = await handleGenerateTicket(seatId)
        if (pdfUrl) {
            const newWindow = window.open(pdfUrl)
            if (newWindow) {
                newWindow.onload = () => {
                    newWindow.print()
                }
            }
        }
    }

    const handleTicketsAction = async (action: () => Promise<void>) => {
        setIsLoadingAll(true)
        try {
            await action()
        } catch (error) {
            console.error('Error during ticket action:', error)
            throw error
        } finally {
            setIsLoadingAll(false)
        }
    }

    const handlePrintAllTickets = async () => {
        return handleTicketsAction(async () => {
            const ticketDetailsList = prepareTicketDetails(selectedSeats)
            if (ticketDetailsList.length === 0) throw new Error('No valid ticket details available for printing')

            const pdfBlob = await generateMergedTickets(ticketDetailsList)
            const url = window.URL.createObjectURL(pdfBlob)
            const newWindow = window.open(url)
            if (newWindow) {
                newWindow.onload = () => {
                    newWindow.print()
                }
            }
        })
    }

    const handleDownloadAllTickets = async () => {
        return handleTicketsAction(async () => {
            const ticketDetailsList = prepareTicketDetails(selectedSeats)
            if (ticketDetailsList.length === 0) throw new Error('No valid ticket details available for downloading')

            const pdfBlob = await generateMergedTickets(ticketDetailsList)
            const url = window.URL.createObjectURL(pdfBlob)

            const normalizedEventName = normalizeName(currentEvent.show.name)
            const uniqueFileName = `jegyek_${normalizedEventName}_${uuidv4()}.pdf`

            downloadFile(url, uniqueFileName)
        })
    }

    return {
        loadingSeatIds,
        isLoadingAll,
        handleGenerateTicket,
        handleDownloadTicket,
        handlePrintTicket,
        handlePrintAllTickets,
        handleDownloadAllTickets,
    }
}
